import { AxiosError, AxiosResponse } from 'axios'
import { AnyAction } from '@reduxjs/toolkit'
import { camelize, snakeize } from 'casing'
import { PlanType } from '@labrav/react-components'
import {
  errorLogger,
  getErrorMessages,
  LoadingTypes,
  startLoading,
  stopLoading,
  updateAppAlert,
} from '@labrav/common'

import {
  getCombinedFormData,
  putCombinedFormData,
} from '../../api/marketplace/index'
import { RequestFailureMessage } from '../../common/utils/messagesContants'
import type { AppDispatch, RootState } from '../../store/types'
import {
  CombineFormActionTypes,
  initialCombinedFormState,
  type CombinedFormState,
} from './types'
import { processCombinedFormData, processGetCombinedFormData } from './utils'

export { CombineFormActionTypes }

export const saveCombinedFormData =
  (
    combinedFormData: CombinedFormState,
    productType: PlanType | undefined,
    sendPostMessage?: (
      labraResellerId: string,
      labraIsvId: string,
      combinedFormId: string,
      externalId: string
    ) => void
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COMBINED_FORM_LOADING))
    try {
      const updatedData = processCombinedFormData(combinedFormData, productType)
      await putCombinedFormData(
        snakeize(updatedData) as Record<string, unknown>
      )
      dispatch(
        updateAppAlert({
          message: 'Offer saved successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      await dispatch(
        getCurrentCombinedFormData(
          updatedData.id as string,
          updatedData.resellerAgreementData.awsProductId as string
        )
      )
      if (sendPostMessage) {
        await sendPostMessage(
          updatedData.labraCpId,
          updatedData.labraIsvId,
          updatedData.id as string,
          updatedData.externalId as string
        )
      }
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.COMBINED_FORM_LOADING))
    }
  }
export const getCurrentCombinedFormData =
  (labraFormId: string, labraProductID: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COMBINED_FORM_LOADING))
    try {
      const { data } = await getCombinedFormData(labraFormId)
      const updatedData = processGetCombinedFormData(
        camelize(data as Record<string, unknown>)
      )
      if (
        updatedData.resellerAgreementData === null ||
        updatedData.cppoData === null
      ) {
        initialCombinedFormState.status = updatedData.status
        initialCombinedFormState.resellerAgreementData.awsProductId =
          labraProductID
        dispatch(setCombinedFormData(initialCombinedFormState))
      } else {
        dispatch(setCombinedFormData(updatedData))
      }
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.COMBINED_FORM_LOADING))
    }
  }

export const setCombinedFormData = (data: CombinedFormState) => {
  return {
    type: CombineFormActionTypes.SET_COMBINED_FORM_DATA,
    payload: data,
  }
}
export const clearCombinedFormData = () =>
  ({
    type: CombineFormActionTypes.CLEAR_COMBINED_FORM_DATA,
  }) as unknown as AnyAction
